import { z } from "zod";
import type { DoseUnit, FormularyKind } from "./catalog";
import type { PurchaseAttachment } from "./inventory";

/**
 * Reading a supplier invoice (PDF, photo, or screenshot) with AI to fill in Record purchase.
 * Nothing is saved from a draft: staff review it in the form and save as usual.
 */

/** Files the AI can read. Photos are resized in the browser before upload. */
export const INVOICE_FILE_TYPES: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};
export const INVOICE_MAX_BYTES = 4_500_000;
export const INVOICE_MAX_FILES = 5;

export const invoiceUploadSchema = z.object({
  name: z.string().trim().min(1).max(200),
  contentType: z.string().refine((t) => t in INVOICE_FILE_TYPES, "Upload a PDF, PNG, JPG, or WebP."),
});

export const readInvoiceSchema = z.object({
  files: z
    .array(z.object({ key: z.string().min(1).max(300), name: z.string().trim().max(200), contentType: z.string().max(100) }))
    .min(1)
    .max(INVOICE_MAX_FILES),
});

/** How a draft line was matched to the formulary. */
export type InvoiceMatch =
  /** The same supplier item # was this product on an earlier purchase. */
  | "history"
  /** The AI's best match by name and strength: check it. */
  | "suggested"
  /** Not in the formulary: a new product to add (suggested details filled in). */
  | "new"
  /** Not tracked in inventory (sharps containers, gloves…): a record only. */
  | "other";

export interface InvoiceDraftLine {
  /** As printed on the invoice. */
  description: string;
  supplierProductId: string;
  match: InvoiceMatch;
  formularyItemId: string | null;
  newProduct: { name: string; drugName: string; kind: FormularyKind; doseUnit: DoseUnit; strengthPerMl: number | null } | null;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  /** As ordered: boxes when perBox is set, else vials or each. */
  quantity: number;
  perBox: number | null;
  /** Per vial (or each), after splitting a box price. */
  unitCents: number | null;
  lotNumber: string;
  expiresOn: string | null;
  /** The AI wasn't sure about something on this line. */
  unsure: string | null;
}

export interface InvoiceDraft {
  supplierId: string | null;
  /** As printed; used to add a new supplier when supplierId is null. */
  supplierName: string;
  invoiceNumber: string;
  orderNumber: string;
  orderedOn: string | null;
  shippingCents: number;
  discountCents: number;
  taxCents: number;
  feesCents: number;
  totalCents: number | null;
  notes: string;
  lines: InvoiceDraftLine[];
  attachments: PurchaseAttachment[];
  /** Things to check before saving (missing lot numbers, totals that don't add up, a possible duplicate…). */
  warnings: string[];
  /** Already recorded: same supplier and invoice (or order) number. */
  duplicateOf: { id: string; label: string } | null;
}
